import React from 'react';
import {
  Monitor,
  Code,
  Brain,
  Database,
  Shield,
  Globe,
  Users,
  BookOpen,
  Award,
  TrendingUp,
  Lightbulb,
  Settings,
  Star,
  Briefcase,
  Smartphone,
  Cloud
} from 'lucide-react';
import '../Css_files/computer.css';
import Navbar from './Navbar';
import Footer from './Footer';

const ComputerScience = () => {
  const programs = [
    {
      title: 'B.Tech Computer Science',
      duration: '4 Years',
      description: 'Comprehensive undergraduate program covering all aspects of computer science and software engineering',
      features: ['Programming Fundamentals', 'Data Structures & Algorithms', 'Software Engineering', 'Final Year Project']
    },
    {
      title: 'M.Tech Artificial Intelligence',
      duration: '2 Years',
      description: 'Advanced study of machine learning, neural networks, and AI system development',
      features: ['Machine Learning', 'Deep Learning', 'Computer Vision', 'Research Projects']
    },
    {
      title: 'M.Tech Cybersecurity',
      duration: '2 Years',
      description: 'Specialized program focusing on information security and digital forensics',
      features: ['Ethical Hacking', 'Network Security', 'Digital Forensics', 'Security Auditing']
    }
  ];

  const faculty = [
    {
      name: 'Dr. Sarah Johnson',
      position: 'Head of Department',
      specialization: 'Artificial Intelligence & Machine Learning',
      experience: '20+ years',
      image: 'https://images.pexels.com/photos/3182821/pexels-photo-3182821.jpeg?auto=compress&cs=tinysrgb&w=300'
    },
    {
      name: 'Dr. Michael Chen',
      position: 'Professor',
      specialization: 'Cybersecurity & Network Systems',
      experience: '18+ years',
      image: 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=300'
    },
    {
      name: 'Dr. Emily Rodriguez',
      position: 'Associate Professor',
      specialization: 'Software Engineering & Cloud Computing',
      experience: '15+ years',
      image: 'https://images.pexels.com/photos/3182824/pexels-photo-3182824.jpeg?auto=compress&cs=tinysrgb&w=300'
    }
  ];

  const specializations = [
    { name: 'Artificial Intelligence', icon: Brain, description: 'Machine learning, neural networks, and intelligent system development.' },
    { name: 'Cybersecurity', icon: Shield, description: 'Information security, ethical hacking, and digital forensics.' },
    { name: 'Web Development', icon: Globe, description: 'Full-stack development, modern frameworks, and web technologies.' },
    { name: 'Mobile Development', icon: Smartphone, description: 'iOS and Android app development, cross-platform solutions.' },
    { name: 'Data Science', icon: Database, description: 'Big data analytics, data visualization, and statistical modeling.' },
    { name: 'Cloud Computing', icon: Cloud, description: 'Cloud architecture, DevOps, and distributed systems.' }
  ];

  const facilities = [
    {
      title: 'AI & Machine Learning Lab',
      description: 'High-performance computing clusters and GPU workstations for AI research',
      icon: <Brain className="computer-facility-icon" />
    },
    {
      title: 'Cybersecurity Lab',
      description: 'Isolated networks and penetration testing environments',
      icon: <Shield className="computer-facility-icon" />
    },
    {
      title: 'Software Development Lab',
      description: 'Modern development environments and collaboration tools',
      icon: <Code className="computer-facility-icon" />
    },
    {
      title: 'Cloud Computing Center',
      description: 'Access to AWS, Azure, and Google Cloud platforms',
      icon: <Cloud className="computer-facility-icon" />
    }
  ];

  const careerStats = [
    { icon: <Users />, value: '99%', label: 'Placement Rate' },
    { icon: <Award />, value: '₹25 LPA', label: 'Average Package' },
    { icon: <Briefcase />, value: '500+', label: 'Industry Partners' },
    { icon: <Star />, value: '4.9/5', label: 'Student Rating' }
  ];

  const testimonials = [
    {
      name: 'Alex Kumar',
      role: 'Software Engineer, Google',
      year: '2021 Graduate',
      content: 'The computer science program provided me with strong fundamentals in algorithms and system design that helped me excel at Google.',
      image: 'https://images.pexels.com/photos/3182773/pexels-photo-3182773.jpeg?auto=compress&cs=tinysrgb&w=150'
    },
    {
      name: 'Lisa Wang',
      role: 'AI Research Scientist, Microsoft',
      year: '2020 Graduate',
      content: 'The AI specialization and research opportunities prepared me perfectly for my career in machine learning and artificial intelligence.',
      image: 'https://images.pexels.com/photos/3182824/pexels-photo-3182824.jpeg?auto=compress&cs=tinysrgb&w=150'
    }
  ];

  return (
    <>
    <Navbar/><br></br><br></br>
    <div className="computer-page">
      {/* Hero Section */}
      <section className="computer-hero">
        <div className="computer-hero-background">
          <div className="computer-animated-code">
            <div className="computer-code computer-code-1"></div>
            <div className="computer-code computer-code-2"></div>
            <div className="computer-code computer-code-3"></div>
            <div className="computer-code computer-code-4"></div>
          </div>
          <div className="computer-floating-data">
            <div className="computer-data computer-data-1"></div>
            <div className="computer-data computer-data-2"></div>
            <div className="computer-data computer-data-3"></div>
            <div className="computer-data computer-data-4"></div>
            <div className="computer-data computer-data-5"></div>
          </div>
        </div>
        <div className="computer-hero-overlay"></div>
        <div className="computer-hero-content">
          <div className="computer-hero-logo">
            <div className="computer-logo-screen">
              <Monitor className="computer-logo-icon" />
            </div>
          </div>
          <h1 className="computer-hero-title">
            Computer Science
            <span className="computer-hero-subtitle">Coding the Future of Technology</span>
          </h1>
          <p className="computer-hero-description">
            Master the art of programming and innovation. From artificial intelligence to cybersecurity, 
            web development to data science - build the digital solutions that will shape tomorrow.
          </p>
          <div className="computer-hero-actions">
            <button className="computer-btn computer-btn-primary">Apply Now</button>
            <button className="computer-btn computer-btn-secondary">Download Brochure</button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="computer-stats">
        <div className="computer-container">
          <div className="computer-stats-grid">
            {careerStats.map((stat, index) => (
              <div key={index} className="computer-stat-card">
                <div className="computer-stat-icon">{stat.icon}</div>
                <div className="computer-stat-value">{stat.value}</div>
                <div className="computer-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="computer-programs">
        <div className="computer-container">
          <div className="computer-section-header">
            <h2 className="computer-section-title">Academic Programs</h2>
            <p className="computer-section-subtitle">
              Comprehensive programs designed to meet industry demands and foster innovation in computer science
            </p>
          </div>
          <div className="computer-programs-grid">
            {programs.map((program, index) => (
              <div key={index} className="computer-program-card">
                <div className="computer-program-header">
                  <h3 className="computer-program-title">{program.title}</h3>
                  <span className="computer-program-duration">{program.duration}</span>
                </div>
                <p className="computer-program-description">{program.description}</p>
                <ul className="computer-program-features">
                  {program.features.map((feature, idx) => (
                    <li key={idx} className="computer-program-feature">
                      <span className="computer-feature-bullet"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="computer-program-btn">Learn More</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Faculty Section */}
      <section className="computer-faculty">
        <div className="computer-container">
          <div className="computer-section-header">
            <h2 className="computer-section-title">Expert Faculty</h2>
            <p className="computer-section-subtitle">
              Learn from industry experts and renowned researchers in computer science
            </p>
          </div>
          <div className="computer-faculty-grid">
            {faculty.map((member, index) => (
              <div key={index} className="computer-faculty-card">
                <div className="computer-faculty-image">
                  <img src={member.image} alt={member.name} />
                  <div className="computer-faculty-overlay">
                    <div className="computer-faculty-experience">{member.experience}</div>
                  </div>
                </div>
                <div className="computer-faculty-info">
                  <h3 className="computer-faculty-name">{member.name}</h3>
                  <p className="computer-faculty-position">{member.position}</p>
                  <p className="computer-faculty-specialization">{member.specialization}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specializations Section */}
      <section className="computer-specializations">
        <div className="computer-container">
          <div className="computer-section-header">
            <h2 className="computer-section-title">Specialization Areas</h2>
            <p className="computer-section-subtitle">
              Explore cutting-edge specializations in computer science that drive technological innovation
            </p>
          </div>
          <div className="computer-specializations-grid">
            {specializations.map((spec, index) => {
              const IconComponent = spec.icon;
              return (
                <div key={index} className="computer-specialization-card">
                  <div className="computer-specialization-icon">
                    <IconComponent className="computer-spec-icon" />
                  </div>
                  <h3 className="computer-specialization-title">{spec.name}</h3>
                  <p className="computer-specialization-description">{spec.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="computer-facilities">
        <div className="computer-container">
          <div className="computer-section-header">
            <h2 className="computer-section-title">Advanced Laboratories</h2>
            <p className="computer-section-subtitle">
              State-of-the-art laboratories equipped with industry-standard equipment for hands-on learning
            </p>
          </div>
          <div className="computer-facilities-grid">
            {facilities.map((facility, index) => (
              <div key={index} className="computer-facility-card">
                <div className="computer-facility-icon-container">
                  {facility.icon}
                </div>
                <h3 className="computer-facility-title">{facility.title}</h3>
                <p className="computer-facility-description">{facility.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section className="computer-research">
        <div className="computer-container">
          <div className="computer-research-content">
            <div className="computer-research-text">
              <h2 className="computer-research-title">Research & Innovation</h2>
              <p className="computer-research-description">
                Our department is at the forefront of computer science research, 
                with ongoing projects in artificial intelligence, quantum computing, 
                blockchain technology, and next-generation software systems.
              </p>
              <div className="computer-research-highlights">
                <div className="computer-research-highlight">
                  <h4>Active Research Projects</h4>
                  <p>50+ ongoing research projects in cutting-edge computer science technologies</p>
                </div>
                <div className="computer-research-highlight">
                  <h4>Publications</h4>
                  <p>300+ research papers published in top-tier international journals</p>
                </div>
                <div className="computer-research-highlight">
                  <h4>Patents</h4>
                  <p>75+ patents filed in software systems and AI technologies</p>
                </div>
              </div>
            </div>
            <div className="computer-research-visual">
              <div className="computer-research-animation">
                <div className="computer-research-node computer-research-node-1"></div>
                <div className="computer-research-node computer-research-node-2"></div>
                <div className="computer-research-node computer-research-node-3"></div>
                <div className="computer-research-center">
                  <Monitor className="computer-research-icon" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="computer-testimonials">
        <div className="computer-container">
          <div className="computer-section-header">
            <h2 className="computer-section-title">Student Success Stories</h2>
            <p className="computer-section-subtitle">
              Hear from our graduates who are making their mark in the technology industry
            </p>
          </div>
          <div className="computer-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="computer-testimonial-card">
                <div className="computer-testimonial-content">
                  <p className="computer-testimonial-text">"{testimonial.content}"</p>
                </div>
                <div className="computer-testimonial-author">
                  <img src={testimonial.image} alt={testimonial.name} className="computer-testimonial-avatar" />
                  <div className="computer-testimonial-info">
                    <h4 className="computer-testimonial-name">{testimonial.name}</h4>
                    <p className="computer-testimonial-role">{testimonial.role}</p>
                    <p className="computer-testimonial-year">{testimonial.year}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="computer-cta">
        <div className="computer-container">
          <div className="computer-cta-content">
            <h2 className="computer-cta-title">Code Your Future</h2>
            <p className="computer-cta-description">
              Join our computer science program and become part of a community 
              that's building the digital technologies that will define the future.
            </p>
            <div className="computer-cta-actions">
              <button className="computer-btn computer-btn-primary computer-btn-large">Apply Now</button>
              <button className="computer-btn computer-btn-outline computer-btn-large">Schedule Lab Tour</button>
            </div>
          </div>
        </div>
      </section>
    </div>
    <Footer/>
    </>
  );
};

export default ComputerScience;